import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ulid, type Item } from '@slipway/shared';
import { Api, loadSession, sha256Hex, type Session } from './lib/api.js';
import { SyncEngine, type ConnState } from './lib/sync.js';
import { EchoGuard, classifyText, writeClipboard } from './lib/clipboard.js';
import {
  chooseFolder,
  downloadBlob,
  humanSize,
  savedFolder,
  saveToFolder,
  supportsDirectoryAccess,
} from './lib/files.js';
import { detectExtension, handOffSession } from './lib/extension.js';
import { Stream } from './components/Stream.js';
import { PairScreen } from './components/PairScreen.js';
import { Toasts, type Toast } from './components/Toasts.js';
import { IconDevices, IconFolder, IconSettings, Mark } from './components/Icons.js';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8787';

export default function App() {
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [items, setItems] = useState<Item[]>([]);
  const [conn, setConn] = useState<ConnState>('offline');
  const [pairCode, setPairCode] = useState<string | null>(null);
  const [pairError, setPairError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [landing, setLanding] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [folder, setFolder] = useState<FileSystemDirectoryHandle | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [hasExtension, setHasExtension] = useState(false);

  const echo = useRef(new EchoGuard());
  const engineRef = useRef<SyncEngine | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingCopy = useRef<string | null>(null);
  const pairingStarted = useRef(false);

  const api = useMemo(() => new Api(API_BASE, session, setSession), []);

  const toast = useCallback((text: string, tone: Toast['tone'] = 'info') => {
    setToasts((t) => [...t, { id: ulid(), text, tone }]);
  }, []);

  /* ------------------------------------------------------- item ingest */

  const ingest = useCallback(
    (incoming: Item[]) => {
      setItems((prev) => {
        const byId = new Map(prev.map((i) => [i.id, i]));
        for (const it of incoming) byId.set(it.id, it);
        return [...byId.values()].sort((a, b) => (a.id < b.id ? 1 : -1));
      });

      const fresh = incoming.filter((i) => i.originDeviceId !== api.getSession()?.deviceId);
      if (fresh.length === 0) return;

      setLanding((s) => new Set([...s, ...fresh.map((i) => i.id)]));
      window.setTimeout(
        () =>
          setLanding((s) => {
            const next = new Set(s);
            for (const i of fresh) next.delete(i.id);
            return next;
          }),
        1000,
      );

      // Newest inbound text becomes the clipboard candidate. If the tab is
      // focused we copy right now; if not we stash it and copy on focus —
      // a background tab is not permitted to touch the clipboard.
      const newestText = [...fresh].reverse().find((i) => i.kind === 'text' || i.kind === 'link');
      if (newestText?.inlineText) {
        const text = newestText.inlineText;
        void writeClipboard(text).then((res) => {
          if (res === 'copied') {
            echo.current.remember(text);
            toast('Copied from iPhone — press Ctrl+V', 'ok');
          } else if (hasExtension) {
            // The extension already wrote it from its offscreen document; the
            // "focus the tab" instruction would be wrong here.
            toast('Copied from iPhone — press Ctrl+V', 'ok');
          } else {
            pendingCopy.current = text;
            toast('New text ready — click this tab to copy it');
          }
        });
      }

      const files = fresh.filter((i) => i.kind === 'file' || i.kind === 'image');
      if (files.length > 0) {
        toast(`${files.length} file${files.length > 1 ? 's' : ''} arrived`, 'ok');
        if (folder) for (const f of files) void receiveFile(f, true);
      }

      if ('Notification' in window && Notification.permission === 'granted' && !document.hasFocus()) {
        const first = fresh[0];
        new Notification('Slipway', {
          body: first.filename ?? first.inlineText?.slice(0, 80) ?? 'New item',
          icon: '/icon-192.png',
          tag: 'slipway',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, folder, toast, hasExtension],
  );

  /* --------------------------------------------------------- lifecycle */

  useEffect(() => {
    void savedFolder().then(setFolder);
    void detectExtension().then(setHasExtension);
  }, []);

  /* Hand the session over so the user pairs once, not twice. */
  useEffect(() => {
    if (!session || !hasExtension) return;
    void handOffSession(session, API_BASE);
  }, [session, hasExtension]);

  useEffect(() => {
    if (!session) return;
    api.setSession(session);
    const engine = new SyncEngine(api, {
      onItems: ingest,
      onState: setConn,
      onRevoked: () => {
        setSession(null);
        setItems([]);
        toast('This browser was removed from your account.', 'error');
      },
    });
    engineRef.current = engine;
    void engine.start();
    return () => engine.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.deviceId]);

  /** Copy-on-focus: the moment you look at the tab, the newest text is yours. */
  useEffect(() => {
    const onFocus = () => {
      const text = pendingCopy.current;
      if (!text) return;
      void writeClipboard(text).then((res) => {
        if (res === 'copied') {
          pendingCopy.current = null;
          echo.current.remember(text);
          toast('Copied — press Ctrl+V', 'ok');
        }
      });
      void engineRef.current?.catchUp();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [toast]);

  /* ------------------------------------------------------------ pairing */

  const beginPairing = useCallback(async () => {
    setPairError(null);
    try {
      const { code } = await api.startPairing(`${navigator.platform || 'Browser'} · Slipway Web`);
      setPairCode(code);
    } catch {
      setPairError('Could not reach the Slipway server.');
    }
  }, [api]);

  /**
   * Exactly one pairing code per unpaired episode. Without this latch a double
   * effect invocation (StrictMode, or any remount) mints a second code and the
   * poller ends up waiting on a different one than the QR is showing — the
   * screen sits on "Waiting for your phone…" forever after a successful scan.
   */
  useEffect(() => {
    if (session) {
      pairingStarted.current = false;
      return;
    }
    if (pairingStarted.current) return;
    pairingStarted.current = true;
    void beginPairing();
  }, [session, beginPairing]);

  useEffect(() => {
    if (!pairCode || session) return;
    const id = window.setInterval(async () => {
      try {
        const res = await api.claimPairing(pairCode);
        if ('pending' in res) return;
        clearInterval(id);
        const next: Session = {
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
          accountId: res.account.id,
          deviceId: res.device.id,
        };
        api.setSession(next);
        setSession(next);
        setPairCode(null);
        toast('Paired. You are connected.', 'ok');
        if ('Notification' in window && Notification.permission === 'default') {
          void Notification.requestPermission();
        }
      } catch {
        clearInterval(id);
        setPairError('That code expired. Generate a new one.');
      }
    }, 1500);
    return () => clearInterval(id);
  }, [pairCode, session, api, toast]);

  /* -------------------------------------------------------------- send */

  const sendText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (echo.current.shouldSuppress(trimmed)) return; // don't bounce it back
      try {
        const { item } = await api.createText(ulid(), trimmed, classifyText(trimmed));
        ingest([item]);
        toast('Sent to your iPhone', 'ok');
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Send failed', 'error');
      }
    },
    [api, ingest, toast],
  );

  const sendFiles = useCallback(
    async (files: FileList | File[]) => {
      for (const file of Array.from(files)) {
        const id = ulid();
        try {
          const hash = await sha256Hex(file);
          const kind = file.type.startsWith('image/') ? 'image' : 'file';
          const res = await api.createBlob({
            id,
            kind,
            filename: file.name,
            mime: file.type || 'application/octet-stream',
            size: file.size,
            sha256: hash,
          });

          if (res.upload && 'url' in res.upload) {
            await api.uploadBytes(res.upload, file);
            const done = await api.completeBlob(res.item.id);
            ingest([done.item]);
          } else {
            ingest([res.item]); // deduped: server already had these bytes
          }
          toast(`Sent ${file.name} (${humanSize(file.size)})`, 'ok');
        } catch (e) {
          toast(`${file.name}: ${e instanceof Error ? e.message : 'upload failed'}`, 'error');
        }
      }
    },
    [api, ingest, toast],
  );

  /* Paste anywhere on the page — no permission prompt, no button to find. */
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (!session) return;
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;

      const files = e.clipboardData?.files;
      if (files && files.length > 0) {
        e.preventDefault();
        void sendFiles(files);
        return;
      }
      const text = e.clipboardData?.getData('text/plain');
      if (text) {
        e.preventDefault();
        void sendText(text);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [session, sendText, sendFiles]);

  /* ----------------------------------------------------------- receive */

  const receiveFile = useCallback(
    async (item: Item, silent = false) => {
      try {
        const { url } = await api.downloadUrl(item.id);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();

        if (folder && item.filename) {
          const result = await saveToFolder(folder, item.filename, blob, async (existing) => {
            if (existing.size !== blob.size) return false;
            return (await sha256Hex(existing)) === item.sha256;
          });
          if (!silent) {
            toast(result.written ? `Saved ${result.name}` : `${result.name} already saved`, 'ok');
          }
        } else if (item.filename) {
          downloadBlob(item.filename, blob);
        }
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Could not save file', 'error');
      }
    },
    [api, folder, toast],
  );

  const copyItem = useCallback(
    async (item: Item) => {
      if (!item.inlineText) return;
      const res = await writeClipboard(item.inlineText);
      if (res === 'copied') {
        echo.current.remember(item.inlineText);
        toast('Copied', 'ok');
      } else {
        toast('Click the page first, then Copy', 'error');
      }
    },
    [toast],
  );

  const openItem = useCallback(
    async (item: Item) => {
      try {
        const { url } = await api.downloadUrl(item.id);
        window.open(url, '_blank', 'noopener');
      } catch {
        toast('Could not open that file', 'error');
      }
    },
    [api, toast],
  );

  const pickFolder = useCallback(async () => {
    const handle = await chooseFolder();
    if (handle) {
      setFolder(handle);
      toast(`Files will save to ${handle.name}`, 'ok');
    }
  }, [toast]);

  /* -------------------------------------------------------------- view */

  if (!session) {
    return (
      <>
        <PairScreen code={pairCode} error={pairError} onRetry={beginPairing} />
        <Toasts toasts={toasts} onExpire={(id) => setToasts((t) => t.filter((x) => x.id !== id))} />
      </>
    );
  }

  return (
    <div className="shell">
      <nav className="rail">
        <Mark className="mark" />
        <span
          className="lamp"
          data-state={conn}
          title={conn === 'online' ? 'Connected' : conn === 'connecting' ? 'Connecting…' : 'Offline'}
          data-testid="lamp"
        />
        <div className="rail-spacer" />
        <button
          className="rail-btn"
          data-active={folder ? 'true' : 'false'}
          onClick={pickFolder}
          title={folder ? `Saving to ${folder.name}` : 'Choose a folder for incoming files'}
          disabled={!supportsDirectoryAccess()}
        >
          <IconFolder />
        </button>
        <button className="rail-btn" onClick={() => setShowSettings(true)} title="Settings">
          <IconSettings />
        </button>
      </nav>

      <main className="main">
        <header className="topbar">
          <h1>Inbox</h1>
          <span className="sub mono">
            {conn === 'online' ? 'live' : conn === 'connecting' ? 'connecting' : 'offline'}
          </span>
          <span className="count mono">{items.length} items</span>
        </header>

        <Stream
          items={items}
          landingIds={landing}
          onCopy={copyItem}
          onSave={(i) => void receiveFile(i)}
          onOpen={(i) => void openItem(i)}
        />

        <footer className="dropbar">
          <div
            className="dropzone"
            data-over={dragOver}
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files.length) void sendFiles(e.dataTransfer.files);
            }}
            data-testid="dropzone"
          >
            Drop files, paste with <span className="kbd">Ctrl</span>
            <span className="kbd">V</span>, or click to choose
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) void sendFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            className="send"
            onClick={async () => {
              try {
                const text = await navigator.clipboard.readText();
                if (text) await sendText(text);
              } catch {
                toast('Press Ctrl+V instead — the browser blocked clipboard reading', 'error');
              }
            }}
          >
            Send clipboard
          </button>
        </footer>
      </main>

      {showSettings && (
        <SettingsPanel
          api={api}
          folder={folder}
          onPickFolder={pickFolder}
          onClose={() => setShowSettings(false)}
          onUnpair={() => {
            api.setSession(null);
            setSession(null);
            setItems([]);
          }}
        />
      )}

      <Toasts toasts={toasts} onExpire={(id) => setToasts((t) => t.filter((x) => x.id !== id))} />
    </div>
  );
}

/* --------------------------------------------------------------- panel */

function SettingsPanel({
  api,
  folder,
  onPickFolder,
  onClose,
  onUnpair,
}: {
  api: Api;
  folder: FileSystemDirectoryHandle | null;
  onPickFolder: () => void;
  onClose: () => void;
  onUnpair: () => void;
}) {
  const [devices, setDevices] = useState<{ id: string; name: string; kind: string }[]>([]);
  const [currentId, setCurrentId] = useState('');

  useEffect(() => {
    void api
      .devices()
      .then((r) => {
        setDevices(r.devices);
        setCurrentId(r.currentDeviceId);
      })
      .catch(() => {});
  }, [api]);

  return (
    <div className="panel" onClick={onClose}>
      <div className="panel-card" onClick={(e) => e.stopPropagation()}>
        <h2>Settings</h2>
        <p className="desc">
          Slipway keeps the last 7 days on the free plan. Older items stay listed but their contents
          are removed.
        </p>

        <div className="setting">
          <div>
            <div className="label">Save files to</div>
            <div className="note">
              {folder ? folder.name : supportsDirectoryAccess() ? 'Not chosen — files download instead' : 'Not supported in this browser'}
            </div>
          </div>
          <button className="act" onClick={onPickFolder} disabled={!supportsDirectoryAccess()}>
            {folder ? 'Change' : 'Choose'}
          </button>
        </div>

        <div className="setting">
          <div>
            <div className="label">Notifications</div>
            <div className="note">Alerts when something arrives while this tab is hidden</div>
          </div>
          <button className="act" onClick={() => void Notification.requestPermission()}>
            {typeof Notification !== 'undefined' && Notification.permission === 'granted'
              ? 'On'
              : 'Enable'}
          </button>
        </div>

        <h2 style={{ marginTop: 22 }}>
          <IconDevices /> Devices
        </h2>
        {devices.map((d) => (
          <div className="device-row" key={d.id}>
            <span className="who">
              {d.name}
              {d.id === currentId && <span className="tag">this one</span>}
            </span>
            {d.id !== currentId && (
              <button
                className="act"
                onClick={async () => {
                  await api.revokeDevice(d.id);
                  setDevices((ds) => ds.filter((x) => x.id !== d.id));
                }}
              >
                Remove
              </button>
            )}
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, marginTop: 22, justifyContent: 'flex-end' }}>
          <button className="act" onClick={onUnpair}>
            Unpair this browser
          </button>
          <button className="send" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
